import type {RegeneracaoEmCurso} from '../../app/PanelProvider.js';

/**
 * O que a aba está esperando depois de um clique em "Regenerar peça".
 *
 * ## O achado
 *
 * `POST /v1/panel/regenerate` responde na hora com uma versão VAZIA, e quem a
 * preenche é a reexecução do dia, que leva minutos. Até 2026-09-06 o painel
 * mostrava um toast dizendo "volta à fila em alguns minutos" e nunca mais
 * falava do assunto (achado F12 da auditoria de 06/09/2026): a peça saía da
 * lista, a aba ficava parada, e a única forma de saber o desfecho era
 * recarregar às cegas. Se a geração falhasse, nada aparecia.
 *
 * Toast some sozinho. Esta faixa fica, porque o que ela descreve continua
 * acontecendo.
 *
 * ## Os quatro estados, e por que nenhum deles é "deu certo"
 *
 * O sucesso não tem faixa: quando a peça volta, o retrato é relido e ela
 * reaparece na fila, que é a prova. Uma faixa verde ali seria uma segunda
 * afirmação sobre a mesma coisa, e a mais fraca das duas.
 *
 * - `aguardando`: a execução está rodando. A fila se atualiza sozinha.
 * - `sem_execucao`: a API respondeu `runId: null`, porque o dia não tem
 *   execução aberta para reexecutar. A versão nova fica VAZIA, e ninguém vai
 *   preenchê-la sozinho. É o caso em que esperar não adianta.
 * - `tempo_esgotado`: a espera DESTA ABA acabou. Não é falha da geração, e a
 *   frase diz isso: a execução pode terminar depois, e o botão relê o retrato.
 * - `falhou`: a execução terminou em falha. A versão nova ficou vazia.
 *
 * O botão de atualizar aparece nos quatro. Ele é a atualização MANUAL que o
 * plano pede, e existe porque nenhuma espera automática cobre o caso em que o
 * dono quer conferir agora.
 */
const MENSAGEM: Record<RegeneracaoEmCurso['estado'], string> = {
  aguardando:
    'Peça em regeneração. A esteira está escrevendo a versão nova, e a fila se atualiza sozinha quando ela ficar pronta.',
  sem_execucao:
    'A versão nova foi aberta e o dia não tem execução para preenchê-la, então ela fica vazia. O runbook editorial explica como abrir o dia.',
  tempo_esgotado:
    'A espera desta aba acabou. Isso não quer dizer que a geração falhou: ela pode terminar depois. Atualize para conferir.',
  falhou:
    'A geração da peça falhou, e a versão nova ficou vazia. O runbook editorial explica onde olhar.',
};

const TOM: Record<RegeneracaoEmCurso['estado'], string> = {
  aguardando: 'dm-alert',
  sem_execucao: 'dm-alert',
  tempo_esgotado: 'dm-alert',
  falhou: 'dm-alert is-error',
};

export const RegenerationBanner = ({
  regeneracao,
  onAtualizar,
}: {
  regeneracao: RegeneracaoEmCurso | null;
  onAtualizar: () => void;
}) => {
  if (!regeneracao) {
    return null;
  }

  return (
    <div
      className={TOM[regeneracao.estado]}
      /*
        `status`, e não `alert`, no estado de espera: `alert` interrompe o leitor
        de tela no meio do que ele estiver lendo, e "a peça está sendo escrita"
        não é urgência. A falha é `alert`, porque essa é.
      */
      role={regeneracao.estado === 'falhou' ? 'alert' : 'status'}
    >
      <span>{MENSAGEM[regeneracao.estado]}</span>{' '}
      <button
        type="button"
        className="dm-editorial-toggle"
        onClick={onAtualizar}
      >
        Atualizar agora
      </button>
    </div>
  );
};
